/* Progressively enhance Shopify's native contact form; no AJAX or synthetic success. */
if (!customElements.get('fitfam-contact')) customElements.define('fitfam-contact', class extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.form = this.querySelector('form[id^="FitFamContact-"]');
    this.panels = [...this.querySelectorAll('[data-step]')];
    this.current = 0;
    this.key = `fitfam-contact:${location.pathname}:${this.dataset.sectionId}`;
    this.draft = this.readDraft();
    const listen = (element, type, fn, options = {}) => element?.addEventListener(type, fn, {...options, signal:this.abort.signal});
    const sent = !!this.querySelector('[data-contact-success]');
    const signup = this.querySelector('[data-signup]');
    if (sent) {
      const optedIn = this.draft?.values?.['contact[Email updates request]'] === true;
      const email = this.draft?.values?.['contact[email]'];
      const signupEmail = signup?.querySelector('input[type=email]');
      if (email && signupEmail) signupEmail.value = email;
      if (signup && !signup.querySelector('[data-signup-result]')) signup.hidden = !optedIn;
      this.querySelectorAll('[data-signup-prompt],[data-open-signup]').forEach(n=>n.hidden=!optedIn);
      this.clearDraft();
      if (this.draft?.pending && !Shopify.designMode) this.openThanks();
    } else if (this.panels.length) {
      // All fields remain visible/required without JS. Enhanced validation reveals errors first.
      this.restoreDraft();
      this.panels.forEach(panel=>{
        if(panel.querySelector('.ff-contact__stage-body'))return;
        const content=document.createElement('div');content.className='ff-contact__stage-body';
        [...panel.children].filter(n=>!n.matches('.ff-contact__actions')).forEach(n=>content.append(n));
        panel.prepend(content);
      });
      this.form.noValidate = true;
      this.setAttribute('data-enhanced','');
      this.dataset.activeStep='0';
      const review=this.querySelector('.ff-contact__summary');
      if(review)review.open=!matchMedia('(max-width: 749px)').matches;
      this.querySelectorAll('[data-enhancement]').forEach(n => n.hidden = false);
      this.panels.forEach((panel, index) => {panel.inert = index !== 0; panel.setAttribute('aria-hidden', String(index !== 0)); panel.classList.toggle('is-active', index === 0);});
      this.orderField();
      this.updateSummary();
      listen(this, 'click', event => {
        if (event.target.closest('[data-next]')) this.next();
        if (event.target.closest('[data-back]')) this.show(Math.max(0,this.current - 1),true);
        const edit = event.target.closest('[data-edit]');
        if (edit) {this.editing = true; this.show(Number(edit.dataset.edit));}
      });
      listen(this.form,'keydown',event => {
        if (event.key === 'Enter' && !event.isComposing && event.target.matches('input:not([type=checkbox]):not([type=radio]):not([type=hidden])')) {event.preventDefault(); this.next();}
      });
      listen(this.form,'input',event => {event.target.setCustomValidity?.(''); event.target.removeAttribute('aria-invalid'); this.updateSummary();});
      listen(this.form,'change',() => {this.orderField(); this.updateSummary();});
      const viewport=()=>{cancelAnimationFrame(this.viewportFrame);this.viewportFrame=requestAnimationFrame(()=>this.sizeForViewport());};
      listen(window,'resize',viewport);
      listen(window.visualViewport,'resize',viewport);
      listen(window,'scroll',viewport);
      listen(this.form,'focusin',viewport);
      this.sizeForViewport();
      listen(this.form,'submit',event => {
        // Shopify's captcha listener invokes native submit even on a cancelled event.
        // Reject invalid/duplicate requests in capture phase, before that listener runs.
        if (this.current !== 3) {event.preventDefault(); event.stopImmediatePropagation(); this.next(); return;}
        if (this.submitting) {event.preventDefault(); event.stopImmediatePropagation(); return;}
        const invalid = this.panels.findIndex((_,index) => !this.valid(index,false));
        if (invalid !== -1) {event.preventDefault(); event.stopImmediatePropagation(); this.show(invalid).then(()=>this.valid(invalid)); return;}
        this.saveDraft();
        this.busy(true);
        // Let Shopify's native contact/captcha flow submit; never retry a POST automatically.
        this.submitTimer = setTimeout(()=>{
          this.busy(false);
          this.querySelector('[data-sending]').textContent = 'Your message hasn’t been confirmed. Complete any verification, or check your connection before trying again. Your details are still here.';
        },30000);
      }, {capture:true});
      listen(window,'pageshow',() => {this.busy(false);});
      if (this.querySelector('[data-server-error]')) {
        this.show(1).then(()=>this.querySelector('[data-server-error]')?.focus());
      } else if (this.draft?.pending) {
        this.show(3);
        this.querySelector('[data-sending]').textContent = 'Your previous submission wasn’t confirmed here. Review your message before sending again.';
      }
    }
    const dialog = this.querySelector('dialog');
    this.querySelectorAll('[data-close-dialog]').forEach(button=>listen(button,'click',()=>dialog.close()));
    listen(this.querySelector('[data-open-signup]'),'click',()=>{dialog.close(); requestAnimationFrame(()=>signup?.querySelector('input[type=email]')?.focus());});
    listen(dialog,'click',event=>{if(event.target===dialog){const r=dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)dialog.close();}});
    listen(dialog,'close',()=>{this.querySelector('[data-contact-success]')?.focus({preventScroll:true});});
    const customerForm = signup?.querySelector('form');
    listen(customerForm,'submit',event=>{
      if (this.signupBusy) {event.preventDefault(); event.stopImmediatePropagation(); return;}
      this.signupBusy = true;
      customerForm.querySelector('button').disabled = true;
      customerForm.setAttribute('aria-busy','true');
      this.signupTimer = setTimeout(()=>{this.signupBusy=false; customerForm.querySelector('button').disabled=false; customerForm.removeAttribute('aria-busy');},30000);
    }, {capture:true});
    listen(window,'pageshow',()=>{
      this.signupBusy=false; clearTimeout(this.signupTimer);
      if(customerForm){customerForm.querySelector('button')?.removeAttribute('disabled'); customerForm.removeAttribute('aria-busy');}
    });
  }
  disconnectedCallback() {
    this.abort?.abort(); this.abort=null;
    clearTimeout(this.submitTimer); clearTimeout(this.signupTimer);
    cancelAnimationFrame(this.viewportFrame);
    this.animations?.forEach(a=>a.cancel());
    this.querySelector('dialog')?.close();
  }
  field(name) {return this.form?.elements.namedItem(name);}
  readDraft() {
    try {const draft=JSON.parse(sessionStorage.getItem(this.key)); return draft?.at > Date.now()-20*60*1000 ? draft : null;} catch (_) {return null;}
  }
  clearDraft() {try {sessionStorage.removeItem(this.key);} catch (_) { /* Storage can be blocked. */ }}
  saveDraft() {
    const values={};
    for (const input of this.form.querySelectorAll('[name^="contact["]')) if (input.type !== 'radio' || input.checked) values[input.name]=input.type==='checkbox' ? input.checked : input.value;
    try {sessionStorage.setItem(this.key,JSON.stringify({at:Date.now(),pending:true,values}));} catch (_) { /* Native submission remains usable. */ }
  }
  restoreDraft() {
    if (!this.draft?.values) return;
    for (const input of this.form.querySelectorAll('[name^="contact["]')) {
      const value=this.draft.values[input.name];
      if (value == null) continue;
      if (input.type === 'checkbox') input.checked=value===true;
      else if (input.type === 'radio') input.checked=input.value===value;
      else if (!input.value) input.value=value;
    }
  }
  orderField() {
    const selected=this.form.querySelector('[data-order-related]:checked');
    const wrapper=this.querySelector('[data-order-field]');
    wrapper.hidden=selected?.dataset.orderRelated !== 'true';
    wrapper.querySelector('input').disabled=wrapper.hidden;
  }
  updateSummary() {
    for (const [key,name] of [['name','contact[name]'],['email','contact[email]'],['inquiry','contact[Inquiry type]']]) {
      const target=this.querySelector(`[data-summary="${key}"]`); if(target)target.textContent=this.field(name)?.value || 'Not selected';
    }
  }
  valid(index, announce=true) {
    const fields=[...this.panels[index].querySelectorAll('input,textarea')].filter(n=>!n.disabled);
    let invalid;
    for(const field of fields) {
      if(['text','textarea'].includes(field.type)) field.setCustomValidity(field.required && !field.value.trim() ? 'Please fill in this field.' : '');
      if(!field.checkValidity() && !invalid)invalid=field;
    }
    if(!invalid)return true;
    if(announce) {
      const error=this.querySelector('[data-validation]');
      error.textContent=invalid.type==='radio' ? 'Please choose what we can help with.' : invalid.type==='email' ? 'Please enter a valid email address.' : index===0 ? 'Please enter your name.' : 'Please add your message.';
      error.hidden=false;
      invalid.setAttribute('aria-invalid','true');
      invalid.setAttribute('aria-describedby',`${invalid.getAttribute('aria-describedby') || ''} ${error.id}`.trim());
      invalid.focus();
    }
    return false;
  }
  next() {if(!this.submitting && this.valid(this.current))this.show(this.editing ? 3 : Math.min(3,this.current+1));}
  sizeForViewport() {
    if(!this.panels?.length)return;
    // Mobile follows document flow; browser chrome and keyboard must not resize the card.
    if(matchMedia('(max-width: 749px)').matches) {
      this.style.removeProperty('--fc-card-space');
      return;
    }
    const card=this.querySelector('.ff-contact__card'),viewport=window.visualViewport;
    const bottom=(viewport?.height || innerHeight)+(viewport?.offsetTop || 0);
    const top=card.getBoundingClientRect().top;
    this.style.setProperty('--fc-card-space',`${Math.max(180,Math.floor(bottom-top-12))}px`);
  }

  async show(index, backwards=false) {
    if(index===this.current)return;
    this.animations?.forEach(a=>a.cancel());
    const previous=this.panels[this.current],next=this.panels[index],direction=backwards||index<this.current ? -1 : 1;
    this.current=index;
    this.dataset.activeStep=String(index);
    this.sizeForViewport();
    this.querySelector('[data-validation]').hidden=true;
    previous.inert=true; previous.setAttribute('aria-hidden','true'); previous.classList.remove('is-active');
    next.inert=false; next.setAttribute('aria-hidden','false'); next.classList.add('is-active');
    this.querySelector('[data-step-label]').textContent=`Step ${index+1} of 4`;
    this.querySelector('progress').value=index+1;
    this.querySelector('[data-stage-announcement]').textContent=`Step ${index+1} of 4. ${next.querySelector('h2').textContent}`;
    if(index===3)this.editing=false;
    this.querySelectorAll('[data-next]').forEach(button=>{button.firstChild.textContent=this.editing ? 'Save and return ' : 'Continue ';});
    this.updateSummary();
    if(matchMedia('(min-width: 750px)').matches && !matchMedia('(prefers-reduced-motion: reduce)').matches && next.animate) {
      previous.classList.add('is-leaving');
      const options={duration:210,easing:'cubic-bezier(.2,.7,.2,1)'};
      this.animations=[previous.animate([{opacity:1,transform:'translateX(0)'},{opacity:0,transform:`translateX(${-direction*16}px)`}],options),next.animate([{opacity:0,transform:`translateX(${direction*16}px)`},{opacity:1,transform:'translateX(0)'}],options)];
      await Promise.allSettled(this.animations.map(a=>a.finished));
      previous.classList.remove('is-leaving');
    }
    if(this.current===index && this.isConnected)next.querySelector('h2').focus({preventScroll:true});
  }
  busy(value) {
    this.submitting=value;
    clearTimeout(this.submitTimer);
    this.form?.setAttribute('aria-busy',String(value));
    this.querySelectorAll('[data-send],[data-next],[data-back],[data-edit]').forEach(n=>n.disabled=value);
    const status=this.querySelector('[data-sending]');if(status)status.textContent=value?'Sending your message… Complete any verification requested.':'';
  }
  openThanks() {
    const dialog=this.querySelector('dialog');
    if(!dialog?.showModal)return;
    dialog.showModal();
    if(!matchMedia('(prefers-reduced-motion: reduce)').matches)dialog.animate([{opacity:0,transform:'translateY(6px)'},{opacity:1,transform:'translateY(0)'}],{duration:180,easing:'ease-out'});
  }
});
